import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Input,
  InputGroup,
  InputRightElement,
  FormControl,
  FormLabel,
  FormHelperText,
  Select,
  Textarea,
  VStack,
  HStack,
  Tag,
  TagLabel,
  TagCloseButton,
  IconButton,
  Text,
  Box,
  useToast,
  Progress,
  Checkbox,
  Divider,
  Code,
  List,
  ListItem,
  ListIcon,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
} from "@chakra-ui/react";
import { CopyIcon, CheckIcon } from "@chakra-ui/icons";
import { useGlobalAction, useFindMany } from "@gadgetinc/react";
import { api } from "../api";

const ShareScenarioModal = ({ isOpen, onClose, scenario, cohort, user }) => {
  const [password, setPassword] = useState("");
  const [emails, setEmails] = useState([]);
  const [currentEmail, setCurrentEmail] = useState("");
  const [useInstitutionalDomain, setUseInstitutionalDomain] = useState(false);
  const [expirationHours, setExpirationHours] = useState("8");
  const [notes, setNotes] = useState("");
  const [generatedLink, setGeneratedLink] = useState(null);
  const [recentlyUsedEmails, setRecentlyUsedEmails] = useState([]);
  const [copied, setCopied] = useState(false);
  const [includePasswordInEmail, setIncludePasswordInEmail] = useState(false);
  const [notifyInstructorOnSubmit, setNotifyInstructorOnSubmit] = useState(true);
  const [selectedCohortId, setSelectedCohortId] = useState('');
  const [selectedInstitution, setSelectedInstitution] = useState('');
  const [enableCloneToInstructors, setEnableCloneToInstructors] = useState(false);
  const [selectedInstructors, setSelectedInstructors] = useState([]);
  const [selectedEvaluationTemplate, setSelectedEvaluationTemplate] = useState('');

  const toast = useToast();
  const [{ data, error, fetching }, createScenarioShareLink] = useGlobalAction(api.createScenarioShareLink);
  const [{ data: templatesData, fetching: fetchingTemplates, error: templatesError }, getTemplates] = useGlobalAction(api.getAvailableEvaluationTemplates);

  // Check if user is admin or system-admin
  const isAdmin = user?.roles?.some(role => ['admin', 'system-admin'].includes(role.key));

  // Fetch user's cohort memberships
  const [{ data: cohortMemberships, fetching: fetchingMemberships, error: membershipsError }] = useFindMany(api.cohortMembership, {
    filter: {
      userId: { equals: user?.id },
      active: { equals: true },
    },
    select: {
      id: true,
      cohort: {
        id: true,
        name: true,
        displayName: true,
        institution: true,
      },
      primaryRole: true,
    },
    pause: !user?.id,
  });

  // Fetch all cohorts (for admin, filtered by institution)
  const [{ data: allCohorts }] = useFindMany(api.cohort, {
    filter: {
      ...(selectedInstitution ? { institution: { equals: selectedInstitution } } : {}),
      isActive: { equals: true },
    },
    select: {
      id: true,
      name: true,
      displayName: true,
      institution: true,
    },
    pause: !isAdmin,
  });

  // Fetch all institutions (for admin dropdown)
  const [{ data: institutions }] = useFindMany(api.institutionConfiguration, {
    select: {
      id: true,
      institution: true,
    },
  });

  // Fetch instructors (when clone is enabled)
  const instructorFilter = enableCloneToInstructors ? {
    filter: {
      ...(selectedInstitution ? { institution: { equals: selectedInstitution } } : {}),
    },
    select: {
      id: true,
      email: true,
      firstName: true,
      lastName: true,
      cohortMemberships: {
        edges: {
          node: {
            cohort: {
              id: true,
              name: true,
            },
            primaryRole: true,
          },
        },
      },
    },
  } : { pause: true };

  const [{ data: instructorsData }] = useFindMany(api.user, instructorFilter);

  // Filter instructors based on cohort selection
  const availableInstructors = instructorsData?.filter(instructor => {
    const memberships = instructor.cohortMemberships?.edges || [];
    return memberships.some(edge => 
      edge.node.primaryRole === 'instructor' &&
      (!selectedCohortId || edge.node.cohort.id === selectedCohortId)
    );
  }) || [];

  // Get user's instructor cohorts
  const instructorCohorts = cohortMemberships?.filter(
    membership => membership.primaryRole === 'instructor'
  ).map(membership => membership.cohort) || [];

  // Determine which cohorts to show
  const displayCohorts = isAdmin ? (allCohorts || []) : instructorCohorts;

  // Get institutional domain from user's email
  const institutionalDomain = user?.email ? '@' + user.email.split('@')[1] : '';

  // Fetch evaluation templates when institution or cohort changes
  useEffect(() => {
    const fetchTemplates = async () => {
      // Fetch templates when:
      // 1. Institution is selected, OR
      // 2. Cohort is selected (including NO_COHORT)
      if (selectedInstitution || selectedCohortId) {
        try {
          await getTemplates({
            institution: selectedInstitution || user?.institution,
            cohortId: selectedCohortId === 'NO_COHORT' ? null : selectedCohortId,
          });
        } catch (err) {
          console.error('Error fetching templates:', err);
        }
      }
    };
    
    fetchTemplates();
  }, [selectedInstitution, selectedCohortId]);

  // Debug logging for cohort memberships
  useEffect(() => {
    if (user?.id) {
      console.log('ShareScenarioModal - User ID:', user.id);
      console.log('ShareScenarioModal - Cohort Memberships:', cohortMemberships);
      console.log('ShareScenarioModal - Instructor Cohorts:', instructorCohorts);
      console.log('ShareScenarioModal - Display Cohorts:', displayCohorts);
    }
  }, [user?.id, cohortMemberships, instructorCohorts, displayCohorts]);

  // Load recently used emails from session storage
  useEffect(() => {
    const stored = sessionStorage.getItem("recentlyUsedEmails");
    if (stored) {
      try {
        setRecentlyUsedEmails(JSON.parse(stored));
      } catch (e) {
        console.error("Failed to parse recently used emails", e);
      }
    }
  }, []);

  // Handle successful link generation
  useEffect(() => {
    if (data) {
      console.log('Share link created successfully. Data received:', data);
      console.log('Emails that should have been sent to:', emails);
      console.log('Generated link:', data.shareUrl || data.url || data.link || data);
      
      setGeneratedLink(data);
      
      // Save emails to recently used
      const updatedRecent = [...new Set([...emails, ...recentlyUsedEmails])].slice(0, 10);
      setRecentlyUsedEmails(updatedRecent);
      sessionStorage.setItem("recentlyUsedEmails", JSON.stringify(updatedRecent));
      
      toast({
        title: "Invitations sent",
        description: `Invitation emails have been sent to ${emails.length} recipient(s): ${emails.join(', ')}`,
        status: "success",
        duration: 7000,
        isClosable: true,
      });
    }
  }, [data]);

  // Handle errors
  useEffect(() => {
    if (error) {
      toast({
        title: "Error creating link",
        description: error.message || "Failed to create share link",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [error]);

  // Handle template errors
  useEffect(() => {
    if (templatesError) {
      toast({
        title: "Error loading templates",
        description: templatesError.message || "Failed to load evaluation templates",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [templatesError]);



  // Email validation
  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Handle adding email
  const handleAddEmail = () => {
    let emailToAdd = currentEmail.trim();
    
    if (!emailToAdd) return;

    // If using institutional domain and email doesn't have @
    if (useInstitutionalDomain && !emailToAdd.includes("@")) {
      emailToAdd = emailToAdd + institutionalDomain;
    }

    if (!isValidEmail(emailToAdd)) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (emails.includes(emailToAdd)) {
      toast({
        title: "Duplicate email",
        description: "This email has already been added",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setEmails([...emails, emailToAdd]);
    setCurrentEmail("");
  };

  // Handle removing email
  const handleRemoveEmail = (emailToRemove) => {
    setEmails(emails.filter((e) => e !== emailToRemove));
  };

  // Handle adding from recent
  const handleAddRecentEmail = (email) => {
    if (!emails.includes(email)) {
      setEmails([...emails, email]);
    }
  };

  // Calculate expiration date
  const getExpirationDate = () => {
    const date = new Date();
    date.setHours(date.getHours() + parseInt(expirationHours));
    return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString();
  };

  // Handle form submission
  const handleGenerateLink = async () => {
    // Validate scenario exists
    if (!scenario || !scenario.id) {
      toast({
        title: "Invalid scenario",
        description: "Please select a valid scenario before sharing",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!password) {
      toast({
        title: "Password required",
        description: "Please enter a password for the share link",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (emails.length === 0) {
      toast({
        title: "Email required",
        description: "Please add at least one email address",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    console.log('=== SHARE SCENARIO SUBMISSION DEBUG ===');
    console.log('Scenario ID:', scenario.id);
    console.log('Emails array:', emails);
    console.log('Email count:', emails.length);
    console.log('Password:', password ? '[SET]' : '[NOT SET]');
    console.log('Full submission payload:', {
      scenarioId: scenario.id,
      password,
      emailAddresses: emails,
      institutionalDomain: useInstitutionalDomain ? institutionalDomain : null,
      expiresAt: new Date(Date.now() + parseInt(expirationHours) * 60 * 60 * 1000).toISOString(),
      notes,
      includePasswordInEmail: includePasswordInEmail,
      notifyInstructorOnSubmit: notifyInstructorOnSubmit,
      cohortId: selectedCohortId,
      selectedInstitution: selectedInstitution || null,
      cloneToInstructors: enableCloneToInstructors ? selectedInstructors : [],
      evaluationTemplateId: selectedEvaluationTemplate || null,
    });
    console.log('=== END DEBUG ===');

    try {
      const result = await createScenarioShareLink({
        scenarioId: scenario.id,
        password,
        emailAddresses: emails,
        institutionalDomain: useInstitutionalDomain ? institutionalDomain : null,
        expiresAt: new Date(Date.now() + parseInt(expirationHours) * 60 * 60 * 1000).toISOString(),
        notes,
        includePasswordInEmail: includePasswordInEmail,
        notifyInstructorOnSubmit: notifyInstructorOnSubmit,
        cohortId: selectedCohortId,
        selectedInstitution: selectedInstitution || null,
        cloneToInstructors: enableCloneToInstructors ? selectedInstructors : [],
        evaluationTemplateId: selectedEvaluationTemplate || null,
      });
      console.log('Action call completed. Result:', result);
    } catch (err) {
      console.error("Error generating link:", err);
      console.error("Error details:", err.message, err.stack);
    }
  };

  // Copy to clipboard
  const handleCopyLink = () => {
    if (generatedLink?.url) {
      navigator.clipboard.writeText(generatedLink.url);
      setCopied(true);
      toast({
        title: "Copied!",
        description: "Link copied to clipboard",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Handle modal close
  const handleClose = () => {
    setPassword("");
    setEmails([]);
    setCurrentEmail("");
    setNotes("");
    setGeneratedLink(null);
    setExpirationHours("8");
    setUseInstitutionalDomain(false);
    setCopied(false);
    setIncludePasswordInEmail(false);
    setNotifyInstructorOnSubmit(true);
    setSelectedCohortId('');
    setSelectedInstitution('');
    setEnableCloneToInstructors(false);
    setSelectedInstructors([]);
    setSelectedEvaluationTemplate('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl">
      <ModalOverlay />
      <ModalContent bg="gray.800" color="white">
        <ModalHeader>Share Scenario</ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Scenario Info */}
            <Box>
              <Text fontWeight="bold" mb={2}>
                {typeof scenario?.title === 'string' ? scenario.title : (scenario?.title?.markdown || "Scenario")}
              </Text>
              {scenario?.description && (
                <Text fontSize="sm" color="gray.400">
                  {typeof scenario.description === 'string' ? scenario.description : (scenario.description?.markdown || scenario.description?.truncatedHTML || '')}
                </Text>
              )}
            </Box>

            <Divider />

            {!generatedLink ? (
              <>
                {/* Access Code Field */}
                <FormControl>
                  <FormLabel>Access Code</FormLabel>
                  <Input
                    type="text"
                    name="scenario-share-modal-password"
                    id="scenario-share-modal-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter access code"
                    bg="gray.700"
                    border="1px solid"
                    borderColor="gray.600"
                    autoComplete="new-password"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    data-lpignore="true"
                    data-form-type="other"
                    data-1p-ignore="true"
                  />
                  <Checkbox
                    isChecked={includePasswordInEmail}
                    onChange={(e) => setIncludePasswordInEmail(e.target.checked)}
                    mt={2}
                  >
                    <Text fontSize="sm">
                      Include access code in email (not recommended - share access code separately for better security)
                    </Text>
                  </Checkbox>
                  <FormHelperText color="gray.400">
                    {includePasswordInEmail ? 'Access code will be included in the email' : 'You will receive a confirmation email with the access code to share separately'}
                  </FormHelperText>
                </FormControl>

                {/* Email Management */}
                <FormControl>
                  <FormLabel>Evaluator Emails</FormLabel>
                  
                  <Checkbox
                    isChecked={useInstitutionalDomain}
                    onChange={(e) => setUseInstitutionalDomain(e.target.checked)}
                    mb={2}
                    isDisabled={!institutionalDomain}
                  >
                    <Text fontSize="sm">
                      Use institutional domain {institutionalDomain && <Code fontSize="xs">{institutionalDomain}</Code>}
                    </Text>
                  </Checkbox>

                  <HStack>
                    <Input
                      value={currentEmail}
                      onChange={(e) => setCurrentEmail(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddEmail();
                        }
                      }}
                      placeholder={useInstitutionalDomain ? "Enter username" : "Enter email address"}
                      bg="gray.700"
                      border="1px solid"
                      borderColor="gray.600"
                    />
                    <Button onClick={handleAddEmail} colorScheme="blue" size="md">
                      Add
                    </Button>
                  </HStack>

                  {/* Email Tags */}
                  {emails.length > 0 && (
                    <Box mt={2} p={2} bg="gray.700" borderRadius="md">
                      <HStack spacing={2} flexWrap="wrap">
                        {emails.map((email, index) => (
                          <Tag key={index} size="md" colorScheme="blue" borderRadius="full">
                            <TagLabel>{email}</TagLabel>
                            <TagCloseButton onClick={() => handleRemoveEmail(email)} />
                          </Tag>
                        ))}
                      </HStack>
                    </Box>
                  )}

                  {/* Recently Used Emails */}
                  {recentlyUsedEmails.length > 0 && (
                    <Box mt={2}>
                      <Text fontSize="sm" color="gray.400" mb={1}>
                        Recently used:
                      </Text>
                      <HStack spacing={2} flexWrap="wrap">
                        {recentlyUsedEmails.map((email, index) => (
                          <Tag
                            key={index}
                            size="sm"
                            variant="outline"
                            colorScheme="gray"
                            cursor="pointer"
                            onClick={() => handleAddRecentEmail(email)}
                          >
                            <TagLabel>{email}</TagLabel>
                          </Tag>
                        ))}
                      </HStack>
                    </Box>
                  )}
                </FormControl>

                {/* Cohort Selector */}
                <FormControl>
                  <FormLabel>Cohort</FormLabel>
                  <Select
                    value={selectedCohortId}
                    onChange={(e) => setSelectedCohortId(e.target.value)}
                    bg="gray.700"
                    border="1px solid"
                    borderColor="gray.600"
                    placeholder="Select cohort for this evaluation"
                  >
                    <option value="NO_COHORT">No Cohort (Use Global Templates)</option>
                    {displayCohorts.map((cohort) => (
                      <option key={cohort.id} value={cohort.id}>
                        {cohort.displayName || cohort.name}
                      </option>
                    ))}
                  </Select>
                  <FormHelperText color="gray.400">
                    Select which cohort this scenario evaluation is for
                  </FormHelperText>
                </FormControl>

                {/* Evaluation Template Selector */}
                <FormControl>
                  <FormLabel>Evaluation Form Type</FormLabel>
                  {fetchingTemplates ? (
                    <Box>
                      <Progress size="xs" isIndeterminate colorScheme="blue" mb={2} />
                      <Text fontSize="sm" color="gray.400">Loading evaluation templates...</Text>
                    </Box>
                  ) : templatesData?.templates && templatesData.templates.length > 0 ? (
                    <>
                      <Select
                        value={selectedEvaluationTemplate}
                        onChange={(e) => setSelectedEvaluationTemplate(e.target.value)}
                        bg="gray.700"
                        border="1px solid"
                        borderColor="gray.600"
                        placeholder="Select evaluation form type"
                      >
                        {templatesData.templates.map((template) => (
                          <option key={template.key} value={template.key}>
                            {template.displayName}
                          </option>
                        ))}
                      </Select>
                      {selectedEvaluationTemplate && templatesData.templates.find(t => t.key === selectedEvaluationTemplate)?.description && (
                        <Box mt={2} p={3} bg="blue.900" borderRadius="md">
                          <Text fontSize="sm" color="blue.100">
                            {templatesData.templates.find(t => t.key === selectedEvaluationTemplate).description}
                          </Text>
                        </Box>
                      )}
                      <FormHelperText color="gray.400">
                        Choose which evaluation form evaluators will use
                      </FormHelperText>
                    </>
                  ) : (
                    <Box p={4} bg="gray.700" borderRadius="md">
                      <Text fontSize="sm" color="gray.400">
                        {selectedCohortId 
                          ? 'No evaluation templates available. Select "No Cohort" to use global templates (NREMT).'
                          : 'Please select a cohort or choose "No Cohort" to see available evaluation templates.'}
                      </Text>
                    </Box>
                  )}
                </FormControl>

                {/* Admin-Only Features */}
                {isAdmin && (
                  <>
                    <Divider />
                    
                    <Box bg="purple.900" p={4} borderRadius="md">
                      <Text fontWeight="bold" mb={3} color="purple.200">
                        Admin Features
                      </Text>
                      
                      {/* Institution Selector */}
                      <FormControl mb={4}>
                        <FormLabel>Institution</FormLabel>
                        <Select
                          value={selectedInstitution}
                          onChange={(e) => {
                            setSelectedInstitution(e.target.value);
                            setSelectedCohortId('');
                            setSelectedInstructors([]);
                          }}
                          bg="gray.700"
                          border="1px solid"
                          borderColor="gray.600"
                          placeholder="Select institution (optional)"
                        >
                          {institutions?.map((inst) => (
                            <option key={inst.id} value={inst.institution}>
                              {inst.institution}
                            </option>
                          ))}
                        </Select>
                        <FormHelperText color="gray.400">
                          Filter cohorts and instructors by institution
                        </FormHelperText>
                      </FormControl>

                      {/* Clone to Instructors */}
                      <FormControl>
                        <Checkbox
                          isChecked={enableCloneToInstructors}
                          onChange={(e) => {
                            setEnableCloneToInstructors(e.target.checked);
                            if (!e.target.checked) {
                              setSelectedInstructors([]);
                            }
                          }}
                        >
                          <Text fontSize="sm" fontWeight="medium">
                            Clone to Instructor Libraries
                          </Text>
                        </Checkbox>
                        <FormHelperText color="gray.400" fontSize="xs">
                          Make this scenario available in selected instructors' personal scenario libraries for future use
                        </FormHelperText>
                      </FormControl>

                      {/* Instructor Multi-Select */}
                      {enableCloneToInstructors && (
                        <Box mt={4}>
                          <FormLabel>Select Instructors</FormLabel>
                          <VStack align="stretch" spacing={2} maxH="200px" overflowY="auto" p={2} bg="gray.700" borderRadius="md">
                            {availableInstructors.length === 0 ? (
                              <Text fontSize="sm" color="gray.400">
                                No instructors found. {selectedCohortId ? 'Try selecting a different cohort.' : 'Try selecting a cohort or institution.'}
                              </Text>
                            ) : (
                              availableInstructors.map((instructor) => (
                                <Checkbox
                                  key={instructor.id}
                                  isChecked={selectedInstructors.includes(instructor.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedInstructors([...selectedInstructors, instructor.id]);
                                    } else {
                                      setSelectedInstructors(selectedInstructors.filter(id => id !== instructor.id));
                                    }
                                  }}
                                >
                                  <Text fontSize="sm">
                                    {instructor.firstName} {instructor.lastName} ({instructor.email})
                                  </Text>
                                </Checkbox>
                              ))
                            )}
                          </VStack>
                          {selectedInstructors.length > 0 && (
                            <Text fontSize="xs" color="gray.400" mt={2}>
                              Selected {selectedInstructors.length} instructor{selectedInstructors.length !== 1 ? 's' : ''}
                            </Text>
                          )}
                        </Box>
                      )}
                    </Box>
                  </>
                )}

                {/* Expiration */}
                <FormControl>
                  <FormLabel>Link Expiration</FormLabel>
                  <NumberInput
                    value={expirationHours}
                    onChange={(valueString) => setExpirationHours(valueString)}
                    min={1}
                    bg="gray.700"
                  >
                    <NumberInputField
                      placeholder="Enter hours until expiration"
                      border="1px solid"
                      borderColor="gray.600"
                    />
                    <NumberInputStepper>
                      <NumberIncrementStepper border="none" />
                      <NumberDecrementStepper border="none" />
                    </NumberInputStepper>
                  </NumberInput>
                  <FormHelperText color="gray.400" fontSize="xs">
                    Examples: 8 hours, 24 hours = 1 day, 72 hours = 3 days, 168 hours = 1 week
                  </FormHelperText>
                  <FormHelperText color="gray.400" mt={2}>
                    Link will expire on: {getExpirationDate()}
                  </FormHelperText>
                </FormControl>

                {/* Notes */}
                <FormControl>
                  <FormLabel>Notes (Optional)</FormLabel>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add any notes or instructions for evaluators"
                    bg="gray.700"
                    border="1px solid"
                    borderColor="gray.600"
                    rows={3}
                  />
                </FormControl>

                {/* Instructor Notification */}
                <FormControl>
                  <Checkbox
                    isChecked={notifyInstructorOnSubmit}
                    onChange={(e) => setNotifyInstructorOnSubmit(e.target.checked)}
                  >
                    <Text fontSize="sm" fontWeight="medium">
                      Notify me when evaluations are submitted
                    </Text>
                  </Checkbox>
                  <FormHelperText color="gray.400">
                    You will receive an email with the completed evaluation as a PDF and formatted in the email body when an evaluator submits their evaluation
                  </FormHelperText>
                </FormControl>
              </>
            ) : (
              <>
                {/* Generated Link Display */}
                <Box>
                  <Text fontWeight="bold" color="green.400" mb={2}>
                    ✓ Invitations Sent Successfully
                  </Text>
                  
                  <FormControl mb={4}>
                    <FormLabel>Shareable URL</FormLabel>
                    <HStack>
                      <Input
                        value={generatedLink.url}
                        isReadOnly
                        bg="gray.700"
                        border="1px solid"
                        borderColor="gray.600"
                      />
                      <IconButton
                        icon={copied ? <CheckIcon /> : <CopyIcon />}
                        onClick={handleCopyLink}
                        colorScheme={copied ? "green" : "blue"}
                        aria-label="Copy link"
                      />
                    </HStack>
                  </FormControl>

                  <Box bg="blue.900" p={4} borderRadius="md" mb={4}>
                    <Text fontWeight="bold" mb={2}>
                      What happens next:
                    </Text>
                    <List spacing={2} fontSize="sm">
                      <ListItem>
                        <ListIcon as={CheckIcon} color="blue.400" />
                        Evaluators will receive an email with the link
                      </ListItem>
                      <ListItem>
                        <ListIcon as={CheckIcon} color="blue.400" />
                        The email includes the password: <Code colorScheme="blue">{password}</Code>
                      </ListItem>
                      <ListItem>
                        <ListIcon as={CheckIcon} color="blue.400" />
                        Link expires: {getExpirationDate()}
                      </ListItem>
                    </List>
                  </Box>

                  <Box>
                    <Text fontWeight="bold" mb={2}>
                      Shared with:
                    </Text>
                    <List spacing={1}>
                      {emails.map((email, index) => (
                        <ListItem key={index} fontSize="sm" color="gray.300">
                          • {email}
                        </ListItem>
                      ))}
                    </List>
                  </Box>

                  {notes && (
                    <Box mt={4}>
                      <Text fontWeight="bold" mb={1}>
                        Notes:
                      </Text>
                      <Text fontSize="sm" color="gray.300">
                        {notes}
                      </Text>
                    </Box>
                  )}
                </Box>
              </>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          {!generatedLink ? (
            <>
              <Button variant="ghost" mr={3} onClick={handleClose}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleGenerateLink}
                isLoading={fetching}
                loadingText="Sending..."
              >
                Send Invitations
              </Button>
            </>
          ) : (
            <Button colorScheme="blue" onClick={handleClose}>
              Done
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ShareScenarioModal;
